import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { QuizService } from "../../services/Quiz.service";
import { handleApiError } from "../../utils/handleApiError";
import { isLoggedIn } from "../../hooks/useAuth";
import { setPendingClaim } from "../../utils/pendingClaim";
import { isSubscriptionError } from "../../services/Subscription.service";
import { useI18n } from "../../i18n";

const DEFAULT_TIME = 15; // kampaniyada vaxt təyin olunmayıbsa

const formatDiscount = (r) =>
  r?.discount_type === "percent"
    ? `${parseFloat(r.value)}%`
    : `${parseFloat(r.value)} AZN`;

export default function PlayPage() {
  const { merchantId, quizId } = useParams();
  const navigate = useNavigate();
  // t → statik mətnlər (dictionary/ui_texts), tr → backend-dən gələn çoxdilli sahələr
  const { t, tr, formatDate } = useI18n();

  // phase: intro | loading | playing | submitting | finished | error | subscription
  const [phase, setPhase] = useState("intro");
  const [gateMessage, setGateMessage] = useState("");
  const [session, setSession] = useState(null); // {session_id, guest_token, quiz, questions}
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_TIME);
  const [selected, setSelected] = useState(null); // seçilən variantın id-si (vizual feedback)
  const [result, setResult] = useState(null);
  const answersRef = useRef([]); // [{question_id, option_id}]
  const lockedIndexRef = useRef(-1); // eyni sual üçün ikiqat advance-in qarşısı

  const quiz = session?.quiz;
  const questions = useMemo(() => session?.questions || [], [session]);
  const perQuestion = quiz?.time_per_question_sec || DEFAULT_TIME;

  // ---- Sessiya başlat ----
  const startSession = async () => {
    setPhase("loading");
    try {
      const data = await QuizService.start({
        merchantId: Number(merchantId),
        quizId: Number(quizId),
      });
      answersRef.current = [];
      lockedIndexRef.current = -1;
      setSession(data);
      setCurrentIndex(0);
      setSelected(null); // əvvəlki oyundan qalan seçimi təmizlə
      setResult(null);
      setTimeLeft(data.quiz?.time_per_question_sec || DEFAULT_TIME);
      setPhase("playing");
    } catch (error) {
      // Abunəlik qapısı: oynamaq üçün aktiv paket tələb olunur
      if (isSubscriptionError(error)) {
        setGateMessage(
          error.response?.data?.message || t("play.sub_required_default")
        );
        setPhase("subscription");
        return;
      }
      toast.error(handleApiError(error, t));
      setPhase("error");
    }
  };

  // ---- Cavabları göndər ----
  const submitAll = useCallback(
    async (finalAnswers) => {
      setPhase("submitting");
      try {
        const res = await QuizService.submit({
          sessionId: session.session_id,
          answers: finalAnswers,
          guestToken: session.guest_token,
        });
        setResult(res);
        setPhase("finished");
      } catch (error) {
        toast.error(handleApiError(error, t));
        setPhase("error");
      }
    },
    [session, t]
  );

  // ---- Növbəti sual / bitir ----
  const advance = useCallback(
    (optionId) => {
      const q = questions[currentIndex];
      if (!q) return;
      // taymer və klik eyni anda işə düşərsə, sual iki dəfə cavablandırılmasın
      if (lockedIndexRef.current === currentIndex) return;
      lockedIndexRef.current = currentIndex;

      const answers = [
        ...answersRef.current,
        { question_id: q.id, option_id: optionId },
      ];
      answersRef.current = answers;

      if (currentIndex + 1 < questions.length) {
        setCurrentIndex((i) => i + 1);
        setTimeLeft(perQuestion);
        setSelected(null);
      } else {
        setSelected(null); // sonuncu sualdan sonra da seçim sıfırlanmalıdır
        submitAll(answers);
      }
    },
    [questions, currentIndex, perQuestion, submitAll]
  );

  // ---- Taymer ----
  useEffect(() => {
    if (phase !== "playing") return;
    if (timeLeft <= 0) {
      advance(null); // vaxt bitdi → cavabsız
      return;
    }
    const timer = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, phase, advance]);

  const handleSelect = (optionId) => {
    if (selected !== null) return; // ikiqat klikin qarşısı
    setSelected(optionId);
    setTimeout(() => advance(optionId), 250);
  };

  // Qonaq qeydiyyata gedir → sessiya yadda saxlanılır, auth-dan sonra avtomatik claim
  const goRegister = (path) => {
    setPendingClaim({
      sessionId: session.session_id,
      guestToken: session.guest_token,
    });
    sessionStorage.setItem("quizzo_redirect", "/coupons");
    navigate(path);
  };

  /* ================= INTRO ================= */
  if (phase === "intro") {
    return (
      <Wrapper>
        <div className="text-6xl mb-4">🎯</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-fuchsia-500 mb-4">
          {t("play.ready_title")}
        </h1>
        <p className="text-gray-600 mb-2 leading-relaxed">
          {t("play.ready_desc")}
        </p>
        {!isLoggedIn() && (
          <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 mb-4 mt-3">
            {t("play.guest_note")}
          </p>
        )}
        <button
          onClick={startSession}
          className="mt-4 bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-bold px-10 py-4 rounded-2xl shadow-lg hover:scale-105 transition-all duration-300 text-lg"
        >
          {t("play.start")}
        </button>
      </Wrapper>
    );
  }

  /* ================= LOADING ================= */
  if (phase === "loading" || phase === "submitting") {
    return (
      <Wrapper>
        <div className="w-14 h-14 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
        <p className="text-gray-600 font-medium">
          {phase === "loading"
            ? t("play.loading_questions")
            : t("play.checking_answers")}
        </p>
      </Wrapper>
    );
  }

  /* ================= ERROR ================= */
  if (phase === "error") {
    return (
      <Wrapper>
        <div className="text-6xl mb-4">😕</div>
        <p className="text-xl font-semibold text-gray-800 mb-4">
          {t("play.error_title")}
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => setPhase("intro")}
            className="bg-indigo-500 text-white font-semibold px-6 py-3 rounded-xl hover:opacity-90 transition"
          >
            {t("common.retry")}
          </button>
          <Link
            to={`/store/${merchantId}`}
            className="bg-gray-100 text-gray-700 font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition"
          >
            {t("common.back")}
          </Link>
        </div>
      </Wrapper>
    );
  }

  /* ================= ABUNƏLİK TƏLƏB OLUNUR ================= */
  if (phase === "subscription") {
    return (
      <Wrapper>
        <div className="text-6xl mb-4">💳</div>
        <h2 className="text-2xl font-extrabold text-gray-800 mb-3">
          {t("play.sub_required_title")}
        </h2>
        <p className="text-gray-600 mb-6">{gateMessage}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={isLoggedIn() ? "/subscription" : "/plans"}
            className="bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-bold px-8 py-3 rounded-xl shadow hover:scale-105 transition"
          >
            {t("play.sub_view_plans")}
          </Link>
          <Link
            to={`/store/${merchantId}`}
            className="bg-gray-100 text-gray-700 font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition"
          >
            {t("common.back")}
          </Link>
        </div>
      </Wrapper>
    );
  }

  /* ================= FINISHED ================= */
  if (phase === "finished" && result) {
    const passedVisual =
      result.coupon || result.requires_registration || result.requires_subscription;

    return (
      <Wrapper wide>
        <div className="text-6xl mb-3">{passedVisual ? "🎉" : "😔"}</div>
        <h2 className="text-3xl font-extrabold text-gray-800 mb-2">
          {passedVisual ? t("play.congrats") : t("play.failed")}
        </h2>
        <p className="text-gray-600 mb-6">
          {t("play.result")}{" "}
          <span className="font-bold text-indigo-600">
            {result.correct}/{result.total}
          </span>{" "}
          {t("play.correct_answers")}{" "}
          <span className="font-bold text-fuchsia-600">
            ({result.score_pct}%)
          </span>
        </p>

        {/* Login müştəri — kupon dərhal */}
        {result.coupon && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-2xl border-2 border-dashed border-amber-300 shadow-inner mb-6">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {t("play.your_coupon")}
            </p>
            <p className="text-4xl font-mono font-extrabold text-indigo-600 tracking-wider">
              {result.coupon.code}
            </p>
            <p className="text-lg font-bold text-green-600 mt-2">
              {t("play.discount_won", { reward: formatDiscount(result.coupon) })}
            </p>
            {result.coupon.expires_at && (
              <p className="text-xs text-gray-500 mt-2">
                {t("play.expires", {
                  date: formatDate(result.coupon.expires_at, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }),
                })}
              </p>
            )}
            <button
              onClick={() => {
                navigator.clipboard?.writeText(result.coupon.code);
                toast.success(t("common.copied"));
              }}
              className="mt-4 bg-indigo-500 text-white text-sm font-semibold px-5 py-2 rounded-xl hover:opacity-90 transition"
            >
              {t("play.copy_code")}
            </button>
          </div>
        )}

        {/* Qonaq — qeydiyyata dəvət */}
        {result.requires_registration && (
          <div className="bg-gradient-to-r from-indigo-50 to-fuchsia-50 p-6 rounded-2xl border border-indigo-200 mb-6">
            <p className="text-lg font-bold text-gray-800 mb-1">
              {t("play.guest_won", { reward: formatDiscount(result.reward_preview) })}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t("play.guest_register_desc")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => goRegister("/register")}
                className="bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-bold px-8 py-3 rounded-xl shadow hover:scale-105 transition"
              >
                {t("play.register_cta")}
              </button>
              <button
                onClick={() => goRegister("/login")}
                className="bg-white border border-indigo-300 text-indigo-600 font-semibold px-8 py-3 rounded-xl hover:bg-indigo-50 transition"
              >
                {t("play.have_account")}
              </button>
            </div>
          </div>
        )}

        {/* Login olub, endirim qazanıb, amma abunəliyi yoxdur */}
        {result.requires_subscription && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-2xl border border-amber-200 mb-6">
            <p className="text-lg font-bold text-gray-800 mb-1">
              {t("play.guest_won", { reward: formatDiscount(result.reward_preview) })}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t("play.sub_claim_desc")}
            </p>
            <button
              onClick={() => {
                // Abunə olduqdan sonra kupon avtomatik alınsın deyə sessiyanı saxlayırıq
                setPendingClaim({
                  sessionId: session.session_id,
                  guestToken: session.guest_token,
                });
                navigate("/subscription");
              }}
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-bold px-8 py-3 rounded-xl shadow hover:scale-105 transition"
            >
              {t("play.sub_claim_cta")}
            </button>
          </div>
        )}

        {/* Qonaq uduzub / login uduzub */}
        {!result.coupon &&
          !result.requires_registration &&
          !result.requires_subscription && (
            <p className="text-gray-500 mb-6">
              {t("play.not_passed")}
            </p>
          )}

        <div className="flex gap-3 justify-center">
          <button
            onClick={() => {
              setResult(null);
              setPhase("intro");
            }}
            className="bg-gray-100 text-gray-700 font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition"
          >
            {t("play.play_again")}
          </button>
          <Link
            to={`/store/${merchantId}`}
            className="bg-gray-100 text-gray-700 font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition"
          >
            {t("play.back_to_store")}
          </Link>
        </div>
      </Wrapper>
    );
  }

  /* ================= PLAYING ================= */
  const question = questions[currentIndex];
  if (!question) return null;

  const progress = ((currentIndex + 1) / questions.length) * 100;
  const timePct = (timeLeft / perQuestion) * 100;
  const timeDanger = timeLeft <= Math.max(3, perQuestion * 0.25);

  return (
    <div className="flex flex-col items-center page pt-6">
      <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 w-full max-w-2xl">
        {/* Başlıq + sayğac */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 truncate pr-4">
            {tr(quiz?.title)}
          </h2>
          <div
            className={`flex items-center justify-center w-14 h-14 rounded-full border-4 font-extrabold text-lg shrink-0 transition-colors ${
              timeDanger
                ? "border-red-400 text-red-500 animate-pulse"
                : "border-indigo-400 text-indigo-600"
            }`}
          >
            {timeLeft}
          </div>
        </div>

        {/* Vaxt zolağı */}
        <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
          <div
            className={`h-2 rounded-full transition-all duration-1000 ease-linear ${
              timeDanger ? "bg-red-400" : "bg-indigo-400"
            }`}
            style={{ width: `${timePct}%` }}
          />
        </div>

        {/* İrəliləyiş */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
          <div
            className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <p className="text-xl text-gray-800 mb-6 text-center font-semibold leading-relaxed">
          {tr(question.title)}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {question.options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => handleSelect(opt.id)}
              disabled={selected !== null}
              className={`p-4 rounded-2xl font-semibold border transition-all duration-200 text-left ${
                selected === opt.id
                  ? "bg-indigo-500 text-white border-indigo-500 scale-[1.02] shadow-lg"
                  : "bg-gray-50 hover:bg-indigo-50 text-gray-700 border-gray-200 hover:border-indigo-300 hover:shadow"
              } ${selected !== null && selected !== opt.id ? "opacity-50" : ""}`}
            >
              {tr(opt.text)}
            </button>
          ))}
        </div>

        <p className="text-sm text-gray-400 text-center mt-6">
          {t("play.question_of", { current: currentIndex + 1, total: questions.length })}
        </p>
      </div>
    </div>
  );
}

/** Mərkəzləşmiş ağ kart */
const Wrapper = ({ children, wide = false }) => (
  <div className="flex justify-center items-center page py-12">
    <div
      className={`bg-white rounded-3xl shadow-2xl p-8 sm:p-10 text-center w-full ${
        wide ? "max-w-2xl" : "max-w-lg"
      }`}
    >
      {children}
    </div>
  </div>
);
